import { Injectable } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { IBenefitsUserDetails } from "@edp-models/benefits.model";
import { ApiService } from "@edp-services/api.service";
import { SetBenefitUserSelected } from "@edp-store/actions/benefits.actions";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { Store } from "@ngrx/store";
import moment from "moment";
import { Subject } from "rxjs";

const KEY_STORAGE_PAGE_COUNTER = "benefit_page_counter";

@Injectable({
    providedIn: "root",
})
export class BenefitsService {
    private scrollPosition = 0;
    private benefitsFilters = [];
    public filterAvailable$ = new Subject<boolean>();
    public filtersAvailable = [
        { name: "pt.edp.edponline.benefits.filters.cardSubtype", optionsValue: ["PARTNER"] },
        { name: "pt.edp.edponline.benefits.filters.status", optionsValue: ["AVAILABLE"] },
        { name: "pt.edp.edponline.benefits.filters.cardType", optionsValue: ["PACKS"] },
        { name: "pt.edp.edponline.benefits.filters.moreFilters", optionsValue: ["PACKS", "AVAILABLE"] },
    ];

    constructor(private apiService: ApiService, private readonly store: Store<IAppEsState>) {}

    public getAvailable(args?: { pn: string }) {
        return this.apiService.callServicePostToCollection("partners.getAvailable", args);
    }

    public getGeneratedCodes() {
        return this.apiService.callServicePostToCollection("partners.getGeneratedCodes", {});
    }

    public submitGiveaway(
        eventCode: string,
        challengeType: string,
        challengeId: number,
        partnerTypeAcronym?: string,
        pn?: string,
        mechanicType?: string
    ) {
        return this.apiService.callServicePostToCollection("EdpE.User.subscribeEvent", {
            eventCode,
            challengeType,
            challengeId,
            eventType: partnerTypeAcronym,
            eventCategory: challengeType,
            pn,
            mechanicType,
        });
    }

    public getCode(
        partner_id,
        option,
        hash_benefits,
        eventName?: string,
        cardSubTypeAcronym?: string,
        partnerTypeAcronym?: string,
        pn?: string,
        mechanicType?: string
    ) {
        return this.apiService.callServicePostToCollection("partners.getCode", {
            partner_id,
            option,
            hash_benefits,
            eventCode: eventName,
            eventCategory: cardSubTypeAcronym,
            eventType: partnerTypeAcronym,
            pn,
            mechanicType,
        });
    }

    public getCards(option, mechanicType: string, eventName: string, cardSubTypeAcronym: string, partnerTypeAcronym: string, pn: string) {
        return this.apiService.callServicePostToCollection("partners.getCards", {
            option,
            mechanicType,
            eventCode: eventName,
            eventCategory: cardSubTypeAcronym,
            eventType: partnerTypeAcronym,
            pn,
        });
    }

    public sendCode(code, partner_id, email, sms, name, partner_type, card_type) {
        return this.apiService.callServicePostToCollection("partners.sendCode", {
            code,
            partner_id,
            email,
            sms,
            name,
            partner_type,
            card_type,
        });
    }

    public getPartnerConditions(args?: any) {
        return this.apiService.callServicePostToCollection("partners.getPartnerConditions", args);
    }

    public setOnboardingDone(args?: any) {
        return this.apiService.callServicePostToCollection("benefits.onboardingView", args);
    }

    public setFilters(filters) {
        this.benefitsFilters = filters;
    }

    public get filters() {
        return this.benefitsFilters;
    }

    public getCounterPartnerBenefits() {
        return this.apiService.callServicePostToCollection("partners.getCounterPartnersActivated", {});
    }

    public getBenefitsUsersList() {
        return this.apiService.callServicePostToCollection("benefits.user", { register: true });
    }

    public validateCodeBP(args) {
        return this.apiService.callServicePostToCollection("pack.validateCodeBP", args);
    }

    public setFilterAvailable(value: boolean): void {
        this.filterAvailable$.next(value);
    }

    public generateTokenBenefits(args?: any) {
        return this.apiService.callServicePost("partners.generateHBenefits", args);
    }

    public getTransactionList(args?: { pn: string }) {
        return this.apiService.callServicePostToCollection("benefits.getTransactionList", args);
    }

    public getDistricts(args: { contractId: string }) {
        return this.apiService.callServicePostToCollection("partners.getDistricts", args);
    }

    public addPageCounter(args?: { pn: string }) {
        return this.apiService.callServicePostToCollection("partners.addEventUsage", args);
    }

    public checkStoragePageCounter(pn: string): boolean {
        const data = this.getStoragePageCounter();
        const itemPnIndex = data.findIndex(i => i.pn === pn);

        if (itemPnIndex === -1) {
            data.push({ pn, date: new Date() });
            this.setStoragePageCounter(data);
            return true;
        }

        const itemPn = data[itemPnIndex];
        const today = moment().startOf("day");
        const lastSetDate = moment(itemPn.date).startOf("day");

        if (lastSetDate.isSameOrAfter(today)) {
            return false;
        }

        itemPn.date = new Date();
        this.setStoragePageCounter(data);
        return true;
    }

    public setStoragePageCounter(data: Array<{ pn; date }>): void {
        localStorage.setItem(KEY_STORAGE_PAGE_COUNTER, JSON.stringify(data));
    }

    public getStoragePageCounter(): Array<{ pn; date }> {
        const data = localStorage.getItem(KEY_STORAGE_PAGE_COUNTER);
        return !!data ? JSON.parse(data) : [];
    }

    private setBenefitUserSelected(item: IBenefitsUserDetails) {
        this.store.dispatch(new SetBenefitUserSelected(item));
    }

    public setUserBenefitByNIF(selectedContract: Contract, listUsersBenefits: IBenefitsUserDetails[]): IBenefitsUserDetails {
        const itemFound = listUsersBenefits.find(list => list.nif === selectedContract.OwnerNif);
        const itemSelected: IBenefitsUserDetails = !!itemFound ? itemFound : listUsersBenefits[0];
        this.setBenefitUserSelected(itemSelected);
        return itemSelected;
    }

    public setScrollPosition(position: number) {
        this.scrollPosition = position;
    }

    public getScrollPosition(): number {
        return this.scrollPosition;
    }
}
